import type { CheckedState } from '@radix-ui/react-checkbox'
import { CheckCheck, Loader2, Search } from 'lucide-react'

import { InputFormComponent } from '@/modules/shared/components/input-form.component';
import { SelectFormComponent } from '@/modules/shared/components/select-form.component';
import { Button } from '@/modules/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/modules/shared/components/ui/card';
import { Checkbox } from '@/modules/shared/components/ui/checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/modules/shared/components/ui/form';
import { Input } from '@/modules/shared/components/ui/input';

import { CustomerChartComponent } from '../components/customer-chart.component'
import {
	formatCepHelper,
	formatCnpjCpfEventHelper,
	formatPhoneHelper,
} from '../helpers/format-fields-customer-form.helper'
import type { useCustomerForm } from '../hooks/use-customer-form.hook'
import type { useCustomerProfile } from '../hooks/use-customer-profile.hook'
import type { TCustomerFormValidation } from '../validation/client-form.validation'

type CustomerFormInterfaceProps = ReturnType<typeof useCustomerForm> &
	ReturnType<typeof useCustomerProfile>

export function CustomerFormInterface({
	form,
	isFieldVisible,
	isFieldEnabled,
	getFieldLabel,
	getFieldMaxLength,
	...props
}: CustomerFormInterfaceProps) {
	const checkBoxItems = [
		{
			label: 'Simples Nacional',
			name: 'simplesNacional',
		},
		{
			label: 'Contribuinte ICMS',
			name: 'contribuinteIcms',
		},
		{
			label: 'Consumidor Final',
			name: 'consumidorFinal',
		},
		{
			label: 'Aplica S.T.',
			name: 'aplicaSt',
		},
	]

	// Helper function to get field configuration
	const getFieldProps = (fieldName: string, defaultLabel: string, defaultMaxLength?: number) => ({
		visible: isFieldVisible(fieldName),
		enabled: isFieldEnabled(fieldName),
		label: getFieldLabel(fieldName, defaultLabel),
		maxLength: getFieldMaxLength(fieldName) || defaultMaxLength,
	})

	return (
		<Form {...form}>
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Dados do cliente</CardTitle>
					<div className="flex text-xs">
						<p>Dados obrigatórios</p>
						<p className="text-red-500">(*)</p>
					</div>
				</CardHeader>
				<CardContent>
					<form onSubmit={form.handleSubmit(props.handleSubmitForm)}>
						<div className="space-y-3">
							{/* Código */}
							{getFieldProps('codigo', 'Código').visible && (
								<InputFormComponent<TCustomerFormValidation>
									label={getFieldProps('codigo', 'Código').label}
									disabled
									control={form.control}
									name="codigo"
								/>
							)}

							{/* CNPJ/CPF */}
							{getFieldProps('cnpjCpf', 'CNPJ/CPF').visible && (
								<div className="flex items-end space-x-2">
									<InputFormComponent<TCustomerFormValidation>
										label={getFieldProps('cnpjCpf', 'CNPJ/CPF').label}
										required
										disabled={props.isViewClient || !getFieldProps('cnpjCpf', 'CNPJ/CPF').enabled}
										control={form.control}
										name="cnpjCpf"
										errorMessage={form.formState.errors.cnpjCpf?.message}
										maxLength={getFieldProps('cnpjCpf', 'CNPJ/CPF', 18).maxLength}
										onKeyUp={(event) => formatCnpjCpfEventHelper(event)}
									/>
									<Button
										disabled={props.isViewClient || !getFieldProps('cnpjCpf', 'CNPJ/CPF').enabled}
										onClick={(e) => {
											e.preventDefault()
											props.checkIfDataOfCnpjOrCpfIsValid()
										}}
									>
										{props.isLoadingCnpj ? (
											<Loader2 size={20} className=" animate-spin text-white" />
										) : (
											<Search size={20} />
										)}
									</Button>
								</div>
							)}

							{/* Nome Cliente */}
							{getFieldProps('nomeCliente', 'Razão Social / Nome').visible && (
								<InputFormComponent<TCustomerFormValidation>
									label={getFieldProps('nomeCliente', 'Razão Social / Nome').label}
									required
									disabled={props.isViewClient || !getFieldProps('nomeCliente', 'Razão Social / Nome').enabled}
									control={form.control}
									name="nomeCliente"
									maxLength={getFieldProps('nomeCliente', 'Razão Social / Nome', 80).maxLength}
									errorMessage={form.formState.errors.nomeCliente?.message}
								/>
							)}

							{/* Frete */}
							{getFieldProps('frete', 'Frete').visible && (
								<SelectFormComponent<TCustomerFormValidation>
									control={form.control}
									name="frete"
									label={getFieldProps('frete', 'Frete').label}
									disabled={props.isViewClient || !getFieldProps('frete', 'Frete').enabled}
									options={props.customerFormTable?.listaFrete?.map((item) => ({
										value: item.idFrete?.toString(),
										label: item.descricao,
									}))}
								/>
							)}

							{/* Faturamento */}
							{getFieldProps('faturamento', 'Tipo faturamento').visible && (
								<SelectFormComponent<TCustomerFormValidation>
									control={form.control}
									name="faturamento"
									label={getFieldProps('faturamento', 'Tipo faturamento').label}
									disabled={props.isViewClient || !getFieldProps('faturamento', 'Tipo faturamento').enabled}
									options={props.customerFormTable?.listaTipoFaturamento?.map(
										(item) => ({
											value: item.idTipoFaturamento?.toString(),
											label: item.descricao,
										}),
									)}
								/>
							)}

							{/* Tabela Preço */}
							{getFieldProps('tabelaPreco', 'Tabela preço').visible && (
								<SelectFormComponent<TCustomerFormValidation>
									control={form.control}
									name="tabelaPreco"
									label={getFieldProps('tabelaPreco', 'Tabela preço').label}
									disabled={props.isViewClient || !getFieldProps('tabelaPreco', 'Tabela preço').enabled}
									options={props.customerFormTable?.listaTabelaPreco?.map(
										(item) => ({
											value: item.idTabelaPreco.toString(),
											label: item.descricao,
										}),
									)}
								/>
							)}

							{/* Grupo - Este campo será oculto se visible for false */}
							{getFieldProps('group', 'Grupo').visible && (
								<SelectFormComponent<TCustomerFormValidation>
									control={form.control}
									name="grupo"
									label={getFieldProps('grupo', 'Grupo').label}
									disabled={props.isViewClient || !getFieldProps('grupo', 'Grupo').enabled}
									options={props.customerFormTable?.listaGrupoCliente?.map(
										(item) => ({
											value: item.idGrupoCliente?.toString(),
											label: item.descricao,
										}),
									)}
								/>
							)}

							{/* Checkboxes */}
							<div className="space-y-2 py-6">
								{checkBoxItems.map((item) => {
									const fieldConfig = getFieldProps(item.name, item.label)
									if (!fieldConfig.visible) return null

									return (
										<FormField
											key={item.name}
											control={form.control}
											name={item.name as keyof TCustomerFormValidation}
											render={({ field }) => {
												return (
													<FormItem
														key={item.name}
														className="flex flex-row items-start space-x-3 space-y-0"
													>
														<FormControl>
															<Checkbox
																{...field}
																disabled={props.isViewClient || !fieldConfig.enabled}
																checked={!!field.value}
																onCheckedChange={(value: CheckedState) => {
																	field.onChange(value)
																}}
																value={
																	field.value as
																	| string
																	| number
																	| readonly string[]
																}
															/>
														</FormControl>
														<FormLabel className="font-normal">
															{fieldConfig.label}
														</FormLabel>
													</FormItem>
												)
											}}
										/>
									)
								})}
							</div>

							{/* Suframa */}
							{getFieldProps('suframa', 'Suframa').visible && (
								<InputFormComponent<TCustomerFormValidation>
									label={getFieldProps('suframa', 'Suframa').label}
									disabled={!getFieldProps('suframa', 'Suframa').enabled}
									maxLength={getFieldProps('suframa', 'Suframa', 11).maxLength}
									control={form.control}
									name="suframa"
									errorMessage={form.formState.errors.suframa?.message}
								/>
							)}

							{/* Última Compra */}
							{getFieldProps('ultimaCompra', 'Última compra').visible && (
								<InputFormComponent<TCustomerFormValidation>
									label={getFieldProps('ultimaCompra', 'Última compra').label}
									disabled={props.isViewClient || !getFieldProps('ultimaCompra', 'Última compra').enabled}
									maxLength={getFieldProps('ultimaCompra', 'Última compra', 11).maxLength}
									control={form.control}
									name="ultimaCompra"
									errorMessage={form.formState.errors.ultimaCompra?.message}
								/>
							)}

							<h2 className="text-md font-bold pt-6">Endereço</h2>

							{/* CEP */}
							{getFieldProps('cepEndereco', 'CEP').visible && (
								<div className="flex items-end space-x-2">
									<InputFormComponent<TCustomerFormValidation>
										label={getFieldProps('cepEndereco', 'CEP').label}
										required
										disabled={props.isViewClient || !getFieldProps('cepEndereco', 'CEP').enabled}
										control={form.control}
										name="cepEndereco"
										maxLength={getFieldProps('cepEndereco', 'CEP', 9).maxLength}
										onKeyUp={(event) => formatCepHelper(event)}
										errorMessage={form.formState.errors.cepEndereco?.message}
									/>
									<Button
										disabled={props.isViewClient || !getFieldProps('cepEndereco', 'CEP').enabled}
										onClick={(e) => {
											e.preventDefault()
											props.handleSearchZipCode()
										}}
									>
										{props.isSearchingCep ? (
											<Loader2 size={20} className=" animate-spin text-white" />
										) : (
											<Search size={20} />
										)}
									</Button>
								</div>
							)}

							{/* Logradouro */}
							{getFieldProps('logradouro', 'Logradouro').visible && (
								<InputFormComponent<TCustomerFormValidation>
									label={getFieldProps('logradouro', 'Logradouro').label}
									required
									control={form.control}
									name="logradouro"
									maxLength={getFieldProps('logradouro', 'Logradouro', 70).maxLength}
									errorMessage={form.formState.errors.logradouro?.message}
									disabled={props.isViewClient || !getFieldProps('logradouro', 'Logradouro').enabled}
								/>
							)}

							{/* Número */}
							{getFieldProps('numero', 'Número').visible && (
								<InputFormComponent<TCustomerFormValidation>
									label={getFieldProps('numero', 'Número').label}
									required
									control={form.control}
									name="numero"
									maxLength={getFieldProps('numero', 'Número', 11).maxLength}
									errorMessage={form.formState.errors.numero?.message}
									disabled={props.isViewClient || !getFieldProps('numero', 'Número').enabled}
								/>
							)}

							{/* Complemento */}
							{getFieldProps('complemento', 'Complemento').visible && (
								<InputFormComponent<TCustomerFormValidation>
									label={getFieldProps('complemento', 'Complemento').label}
									control={form.control}
									name="complemento"
									maxLength={getFieldProps('complemento', 'Complemento', 30).maxLength}
									errorMessage={form.formState.errors.complemento?.message}
									disabled={props.isViewClient || !getFieldProps('complemento', 'Complemento').enabled}
								/>
							)}

							{/* Bairro */}
							{getFieldProps('bairro', 'Bairro').visible && (
								<InputFormComponent<TCustomerFormValidation>
									label={getFieldProps('bairro', 'Bairro').label}
									required
									control={form.control}
									name="bairro"
									maxLength={getFieldProps('bairro', 'Bairro', 50).maxLength}
									errorMessage={form.formState.errors.bairro?.message}
									disabled={props.isViewClient || !getFieldProps('bairro', 'Bairro').enabled}
								/>
							)}

							{/* Estado */}
							{getFieldProps('estado', 'Estado').visible && (
								<SelectFormComponent<TCustomerFormValidation>
									control={form.control}
									name="estado"
									required
									label={getFieldProps('estado', 'Estado').label}
									onChange={(value) => props.handleChangeStateAndLoadCity(value)}
									disabled={props.isViewClient || !getFieldProps('estado', 'Estado').enabled}
									options={props.customerFormTable?.listaUF?.map((item) => ({
										value: item.idUf?.toString(),
										label: item.uf,
									}))}
								/>
							)}

							{/* Cidade */}
							{getFieldProps('cidade', 'Cidade').visible && (
								<SelectFormComponent<TCustomerFormValidation>
									control={form.control}
									name="cidade"
									required
									label={getFieldProps('cidade', 'Cidade').label}
									disabled={props.isViewClient || !getFieldProps('cidade', 'Cidade').enabled}
									options={props.customerFormCities?.map((item) => ({
										value: item.id?.toString(),
										label: item.nome,
									}))}
								/>
							)}

							<h2 className="text-md font-bold pt-6">Contato</h2>

							{/* Nome do Contato */}
							{getFieldProps('contato', 'Nome do contato').visible && (
								<InputFormComponent<TCustomerFormValidation>
									label={getFieldProps('contato', 'Nome do contato').label}
									control={form.control}
									name="contato"
									maxLength={getFieldProps('contato', 'Nome do contato', 50).maxLength}
									disabled={props.isViewClient || !getFieldProps('contato', 'Nome do contato').enabled}
									errorMessage={form.formState.errors.contato?.message}
								/>
							)}

							{/* Telefone */}
							{getFieldProps('telefone', 'Fone - (DDD)').visible && (
								<FormField
									control={form.control}
									name="telefone"
									render={({ field }) => (
										<FormItem className="flex flex-col flex-1">
											<FormLabel className="flex font-normal">
												{getFieldProps('telefone', 'Fone - (DDD)').label}
											</FormLabel>
											<FormControl>
												<Input
													{...field}
													maxLength={getFieldProps('telefone', 'Fone - (DDD)', 15).maxLength}
													minLength={14}
													disabled={props.isViewClient || !getFieldProps('telefone', 'Fone - (DDD)').enabled}
													onChange={(e) => {
														formatPhoneHelper(e);
														field.onChange(e);
													}}
												/>
											</FormControl>
										</FormItem>
									)}
								/>
							)}

							{/* E-mail */}
							{getFieldProps('email', 'E-mail').visible && (
								<InputFormComponent<TCustomerFormValidation>
									label={getFieldProps('email', 'E-mail').label}
									control={form.control}
									name="email"
									maxLength={getFieldProps('email', 'E-mail', 50).maxLength}
									type="email"
									inputMode="email"
									disabled={props.isViewClient || !getFieldProps('email', 'E-mail').enabled}
									errorMessage={form.formState.errors.email?.message}
								/>
							)}

							{!props.isViewClient && (
								<div className="flex flex-1 justify-end">
									<Button className="bg-green-500 hover:bg-green-600">
										<div className="flex items-center">
											{props.isSubmiting ? (
												<Loader2 className="h-6 w-6 mr-2 animate-spin text-white" />
											) : (
												<CheckCheck className="h-6 w-6 mr-2" />
											)}
											<p>Salvar</p>
										</div>
									</Button>
								</div>
							)}
							{props.isViewClient && <CustomerChartComponent {...props} />}
						</div>
					</form>
				</CardContent>
			</Card>
		</Form>
	)
}